<template>
  <!-- Header -->
  <div class="header w-full bg-gray-900 p-3 border-b border-gray-800">
    <div class="title-container flex justify-center">
      <h1 class="text-xl font-bold text-indigo-300 tracking-wide">ประวัติการกระทำ</h1>
    </div>
  </div>
  <div class="p-3 pb-20 bg mb-auto h-full overflow-auto bg-gradient-to-b from-gray-800 text-white" id="style-1">
    <!-- Logs List -->
    <div class="logs-list max-w-3xl mx-auto">
      <div v-if="loading" class="loading-state bg-gray-700 border border-gray-600 p-4 rounded-lg text-center text-indigo-200 text-sm animate-pulse">
        <p>กำลังโหลด...</p>
      </div>
      <div v-else-if="logs.length === 0" class="empty-state bg-gray-700 border border-gray-600 p-4 rounded-lg text-center text-indigo-200 text-sm">
        <p>ไม่พบประวัติการกระทำ</p>
      </div>
      <div
        v-else
        v-for="log in logs"
        :key="log.id"
        class="log-card bg-gray-700 border border-gray-600 rounded-lg p-4 mb-4 shadow-xl transition-all duration-300 hover:shadow-2xl hover:scale-105 hover:border-indigo-500 animate-fade-in"
        :class="{ 'border-l-4 border-green-500': log.success, 'border-l-4 border-red-500': !log.success }"
      >
        <div class="log-header flex justify-between items-center mb-2">
          <span class="log-action text-sm font-semibold text-indigo-300 flex items-center">
            <i class="fas fa-scroll mr-2 text-indigo-400"></i>
            {{ getActionName(log.action) }}
          </span>
          <span class="log-timestamp text-xs text-indigo-200">{{ formatTimestamp(log.timestamp) }}</span>
        </div>
        <p class="log-message text-sm text-white mb-2">{{ log.displayMessage }}</p>
        
        <div v-if="user.uid === 1" class="log-details text-xs text-indigo-200 space-y-1">
          <!-- General Details -->
          <p v-if="log.details.characterName">
            <i class="fas fa-user mr-1 text-indigo-400"></i> ชื่อตัวละคร: {{ log.details.characterName }}
          </p>
          <p v-if="log.details.traitName">
            <i class="fas fa-star mr-1 text-indigo-400"></i> ลักษณะนิสัย: {{ log.details.traitName }}
          </p>
          <p v-if="log.details.usedPoints">
            <i class="fas fa-coins mr-1 text-indigo-400"></i> แต้มที่ใช้: {{ log.details.usedPoints }}
          </p>
          <!-- Explore Details -->
          <p v-if="log.details.mapName">
            <i class="fas fa-map mr-1 text-indigo-400"></i> แผนที่: {{ log.details.mapName }}
          </p>
          <p v-if="log.details.resourcesUsed && Object.keys(log.details.resourcesUsed).length > 0">
            <i class="fas fa-box mr-1 text-indigo-400"></i> ทรัพยากรที่ใช้:
            {{ formatResources(log.details.resourcesUsed) }}
          </p>
          <p v-if="log.details.zombieNames && log.details.zombieNames.length > 0">
            <i class="fas fa-skull mr-1 text-indigo-400"></i> ซอมบี้ที่พบ:
            {{ log.details.zombieNames.map(getZombieName).join(', ') }}
          </p>
          <p v-if="log.details.rewardItemNames && log.details.rewardItemNames.length > 0">
            <i class="fas fa-gift mr-1 text-indigo-400"></i> รางวัลที่ได้รับ:
            {{ log.details.rewardItemNames.join(', ') }}
          </p>
          <!-- Attack Zombie and Flee Fight Details -->
          <p v-if="log.details.zombieName">
            <i class="fas fa-skull mr-1 text-indigo-400"></i> ซอมบี้: {{ getZombieName(log.details.zombieName) }}
          </p>
          <p v-if="log.details.zombieMaxHp">
            <i class="fas fa-heart mr-1 text-indigo-400"></i> HP ซอมบี้: {{ log.details.zombieMaxHp }}
          </p>
          <p v-if="log.details.zombieAtk">
            <i class="fas fa-fist-raised mr-1 text-indigo-400"></i> พลังโจมตีซอมบี้: {{ log.details.zombieAtk }}
          </p>
          <p v-if="log.details.zombieCritical">
            <i class="fas fa-bolt mr-1 text-indigo-400"></i> โอกาสคริติคอลซอมบี้:
            {{ (log.details.zombieCritical * 100).toFixed(2) }}%
          </p>
          <p v-if="log.details.zombieEvasion">
            <i class="fas fa-wind mr-1 text-indigo-400"></i> โอกาสหลบซอมบี้:
            {{ (log.details.zombieEvasion * 100).toFixed(2) }}%
          </p>
          <p v-if="log.details.damageToZombie">
            <i class="fas fa-burst mr-1 text-indigo-400"></i> ดาเมจที่ทำ: {{ log.details.damageToZombie }}
          </p>
          <p v-if="log.details.damageToPlayer">
            <i class="fas fa-shield-alt mr-1 text-indigo-400"></i> ดาเมจที่ได้รับ: {{ log.details.damageToPlayer }}
          </p>
          <p v-if="log.details.isCritical">
            <i class="fas fa-bolt mr-1 text-indigo-400"></i> คริติคอล:
            {{ log.details.isCritical ? 'ใช่' : 'ไม่' }}
          </p>
          <p v-if="log.details.isZombieCritical">
            <i class="fas fa-bolt mr-1 text-indigo-400"></i> ซอมบี้คริติคอล:
            {{ log.details.isZombieCritical ? 'ใช่' : 'ไม่' }}
          </p>
          <p v-if="log.details.isEvaded">
            <i class="fas fa-wind mr-1 text-indigo-400"></i> ซอมบี้หลบ:
            {{ log.details.isEvaded ? 'ใช่' : 'ไม่' }}
          </p>
          <p v-if="log.details.isPlayerEvaded">
            <i class="fas fa-wind mr-1 text-indigo-400"></i> ผู้เล่นหลบ:
            {{ log.details.isPlayerEvaded ? 'ใช่' : 'ไม่' }}
          </p>
          <p v-if="log.details.playerEvasion">
            <i class="fas fa-wind mr-1 text-indigo-400"></i> โอกาสหลบ (Evasion):
            {{ (log.details.playerEvasion * 100).toFixed(2) }}%
          </p>
          <p v-if="log.details.playerFlee">
            <i class="fas fa-running mr-1 text-indigo-400"></i> คะแนนหนี (Flee): {{ log.details.playerFlee }}
          </p>
          <p v-if="log.details.zombieFlee">
            <i class="fas fa-running mr-1 text-indigo-400"></i> คะแนนหนีของซอมบี้: {{ log.details.zombieFlee }}
          </p>
          <p v-if="log.details.fleeThreshold">
            <i class="fas fa-percentage mr-1 text-indigo-400"></i> โอกาสหนีสำเร็จ: {{ log.details.fleeThreshold }}%
          </p>
          <p v-if="log.details.isFleeSuccessful !== undefined">
            <i class="fas fa-running mr-1 text-indigo-400"></i> หนีสำเร็จ:
            {{ log.details.isFleeSuccessful ? 'ใช่' : 'ไม่' }}
          </p>
          <p v-if="log.details.isEvadeSuccessful !== undefined">
            <i class="fas fa-wind mr-1 text-indigo-400"></i> หลบการโจมตี:
            {{ log.details.isEvadeSuccessful ? 'ใช่' : 'ไม่' }}
          </p>
          <p v-if="log.details.gainedExp">
            <i class="fas fa-star mr-1 text-indigo-400"></i> EXP ที่ได้รับ: {{ log.details.gainedExp }}
          </p>
          <p v-if="log.details.gainedSilver">
            <i class="fas fa-coins mr-1 text-indigo-400"></i> เหรียญเงินที่ได้รับ: {{ log.details.gainedSilver }}
          </p>
          <p v-if="log.details.newLevel">
            <i class="fas fa-level-up-alt mr-1 text-indigo-400"></i> เลเวลใหม่: {{ log.details.newLevel }}
          </p>
          <p v-if="log.details.infectionLevel">
            <i class="fas fa-biohazard mr-1 text-indigo-400"></i> ระดับการติดเชื้อ: {{ log.details.infectionLevel }}
          </p>
          <p v-if="log.details.isPlayerDead">
            <i class="fas fa-skull-crossbones mr-1 text-indigo-400"></i> ผู้เล่นตาย:
            {{ log.details.isPlayerDead ? 'ใช่' : 'ไม่' }}
          </p>
          <p v-if="log.details.bornEventId">
            <i class="fas fa-baby mr-1 text-indigo-400"></i> รหัสอีเวนต์เกิดใหม่: {{ log.details.bornEventId }}
          </p>
          <p v-if="log.details.eventId">
            <i class="fas fa-calendar-alt mr-1 text-indigo-400"></i> รหัสเหตุการณ์: {{ log.details.eventId }}
          </p>
          <!-- Item-related Details -->
          <p v-if="log.details.itemName">
            <i class="fas fa-suitcase mr-1 text-indigo-400"></i> ไอเทม: {{ log.details.itemName }}
          </p>
          <p v-if="log.details.itemType">
            <i class="fas fa-tag mr-1 text-indigo-400"></i> ประเภทไอเทม: {{ log.details.itemType }}
          </p>
          <p v-if="log.details.itemWeight">
            <i class="fas fa-weight-hanging mr-1 text-indigo-400"></i> น้ำหนักไอเทม: {{ log.details.itemWeight }}
          </p>
          <p v-if="log.details.newItemStats">
            <i class="fas fa-chart-bar mr-1 text-indigo-400"></i> สถิติใหม่:
            {{ formatItemStats(log.details.newItemStats) }}
          </p>
          <p v-if="log.details.itemNames && log.details.itemNames.length > 0">
            <i class="fas fa-suitcase mr-1 text-indigo-400"></i> ไอเทมที่เพิ่ม:
            {{ log.details.itemNames.join(', ') }}
          </p>
          <p v-if="log.details.totalWeight">
            <i class="fas fa-weight-hanging mr-1 text-indigo-400"></i> น้ำหนักรวม: {{ log.details.totalWeight }}
          </p>
          <!-- Trait, Skill, Tattoo, Title, Heal, Shop, Food, Room, and Assassination Details -->
          <p v-if="log.details.traitStats">
            <i class="fas fa-star mr-1 text-indigo-400"></i> สถิติลักษณะนิสัย:
            {{ formatItemStats(log.details.traitStats) }}
          </p>
          <p v-if="log.details.usedCash">
            <i class="fas fa-coins mr-1 text-indigo-400"></i> แคชที่ใช้: {{ log.details.usedCash }}
          </p>
          <p v-if="log.details.newDamageTaken">
            <i class="fas fa-heart-broken mr-1 text-indigo-400"></i> ความเสียหายใหม่: {{ log.details.newDamageTaken }}
          </p>
          <p v-if="log.details.skillName">
            <i class="fas fa-book mr-1 text-indigo-400"></i> ทักษะ: {{ log.details.skillName }}
          </p>
          <p v-if="log.details.newSkillStats">
            <i class="fas fa-chart-bar mr-1 text-indigo-400"></i> สถิติทักษะใหม่:
            {{ formatItemStats(log.details.newSkillStats) }}
          </p>
          <p v-if="log.details.tattooName">
            <i class="fas fa-tattoo mr-1 text-indigo-400"></i> ลายสัก: {{ log.details.tattooName }}
          </p>
          <p v-if="log.details.tattooStats">
            <i class="fas fa-chart-bar mr-1 text-indigo-400"></i> สถิติลายสัก:
            {{ formatItemStats(log.details.tattooStats) }}
          </p>
          <p v-if="log.details.usedCurrency && log.details.usedAmount">
            <i class="fas fa-coins mr-1 text-indigo-400"></i> ใช้สกุลเงิน:
            {{ formatCurrency(log.details.usedCurrency, log.details.usedAmount) }}
          </p>
          <p v-if="log.details.newBalances">
            <i class="fas fa-wallet mr-1 text-indigo-400"></i> ยอดเงินคงเหลือ:
            {{ formatBalances(log.details.newBalances) }}
          </p>
          <p v-if="log.details.titleName">
            <i class="fas fa-crown mr-1 text-indigo-400"></i> ฉายา: {{ log.details.titleName }}
          </p>
          <p v-if="log.details.newTitleStats">
            <i class="fas fa-chart-bar mr-1 text-indigo-400"></i> สถิติฉายาใหม่:
            {{ formatItemStats(log.details.newTitleStats) }}
          </p>
          <p v-if="log.details.usedRenown">
            <i class="fas fa-star mr-1 text-indigo-400"></i> Renown ที่ใช้: {{ log.details.usedRenown }}
          </p>
          <p v-if="log.details.usedGold">
            <i class="fas fa-coins mr-1 text-indigo-400"></i> ทองที่ใช้: {{ log.details.usedGold }}
          </p>
          <p v-if="log.details.usedSilver">
            <i class="fas fa-coins mr-1 text-indigo-400"></i> เหรียญเงินที่ใช้: {{ log.details.usedSilver }}
          </p>
          <p v-if="log.details.usedDiamond">
            <i class="fas fa-gem mr-1 text-indigo-400"></i> เพชรที่ใช้: {{ log.details.usedDiamond }}
          </p>
          <p v-if="log.details.previousDamageTaken">
            <i class="fas fa-heart-broken mr-1 text-indigo-400"></i> ความเสียหายก่อนรักษา: {{ log.details.previousDamageTaken }}
          </p>
          <p v-if="log.details.newHealCount">
            <i class="fas fa-medkit mr-1 text-indigo-400"></i> จำนวนครั้งการรักษาที่เหลือ: {{ log.details.newHealCount }}
          </p>
          <p v-if="log.details.previousInfectionLevel">
            <i class="fas fa-biohazard mr-1 text-indigo-400"></i> ระดับการติดเชื้อก่อนรักษา: {{ log.details.previousInfectionLevel }}
          </p>
          <p v-if="log.details.newInfectionLevel">
            <i class="fas fa-biohazard mr-1 text-indigo-400"></i> ระดับการติดเชื้อใหม่: {{ log.details.newInfectionLevel }}
          </p>
          <p v-if="log.details.newSilver">
            <i class="fas fa-coins mr-1 text-indigo-400"></i> เหรียญเงินคงเหลือ: {{ log.details.newSilver }}
          </p>
          <p v-if="log.details.gainedSilver">
            <i class="fas fa-coins mr-1 text-indigo-400"></i> เหรียญเงินที่ได้รับ: {{ log.details.gainedSilver }}
          </p>
          <p v-if="log.details.newBalance">
            <i class="fas fa-wallet mr-1 text-indigo-400"></i> ยอดเงินคงเหลือ: {{ log.details.newBalance }}
          </p>
          <p v-if="log.details.shopItemId">
            <i class="fas fa-shopping-cart mr-1 text-indigo-400"></i> รหัสไอเทมร้านค้า: {{ log.details.shopItemId }}
          </p>
          <p v-if="log.details.foodName">
            <i class="fas fa-utensils mr-1 text-indigo-400"></i> อาหาร: {{ log.details.foodName }}
          </p>
          <p v-if="log.details.foodLevelIncrease">
            <i class="fas fa-bread-slice mr-1 text-indigo-400"></i> ระดับอาหารที่เพิ่ม: {{ log.details.foodLevelIncrease }}
          </p>
          <p v-if="log.details.newFoodLevel">
            <i class="fas fa-bread-slice mr-1 text-indigo-400"></i> ระดับอาหารใหม่: {{ log.details.newFoodLevel }}
          </p>
          <p v-if="log.details.happyLevelChange">
            <i class="fas fa-smile mr-1 text-indigo-400"></i> การเปลี่ยนแปลงความสุข: {{ log.details.happyLevelChange }}
          </p>
          <p v-if="log.details.newHappyLevel">
            <i class="fas fa-smile mr-1 text-indigo-400"></i> ระดับความสุขใหม่: {{ log.details.newHappyLevel }}
          </p>
          <p v-if="log.details.roomNumber">
            <i class="fas fa-door-open mr-1 text-indigo-400"></i> ห้อง: {{ log.details.roomNumber }}
          </p>
          <p v-if="log.details.newRoom">
            <i class="fas fa-door-open mr-1 text-indigo-400"></i> ห้องใหม่: {{ log.details.newRoom }}
          </p>
          <p v-if="log.details.newGold">
            <i class="fas fa-coins mr-1 text-indigo-400"></i> ทองคงเหลือ: {{ log.details.newGold }}
          </p>
          <p v-if="log.details.newDiamond">
            <i class="fas fa-gem mr-1 text-indigo-400"></i> เพชรคงเหลือ: {{ log.details.newDiamond }}
          </p>
          <!-- Assassination Details -->
          <p v-if="log.details.criminalName">
            <i class="fas fa-user-secret mr-1 text-indigo-400"></i> อาชญากร: {{ log.details.criminalName }}
          </p>
          <p v-if="log.details.criminalRoom">
            <i class="fas fa-door-open mr-1 text-indigo-400"></i> ห้องของอาชญากร: {{ log.details.criminalRoom }}
          </p>
          <p v-if="log.details.selectedRoom">
            <i class="fas fa-door-open mr-1 text-indigo-400"></i> ห้องที่เลือก: {{ log.details.selectedRoom }}
          </p>
          <p v-if="log.details.actualRoom">
            <i class="fas fa-door-open mr-1 text-indigo-400"></i> ห้องจริง: {{ log.details.actualRoom }}
          </p>
          <p v-if="log.details.diceCount">
            <i class="fas fa-dice mr-1 text-indigo-400"></i> จำนวนลูกเต๋า: {{ log.details.diceCount }}
          </p>
          <p v-if="log.details.playerDiceResults && log.details.playerDiceResults.length > 0">
            <i class="fas fa-dice mr-1 text-indigo-400"></i> ผลลูกเต๋าผู้เล่น: {{ log.details.playerDiceResults.join(' + ') }}
          </p>
          <p v-if="log.details.totalPlayerDice">
            <i class="fas fa-dice mr-1 text-indigo-400"></i> ผลรวมลูกเต๋าผู้เล่น: {{ log.details.totalPlayerDice }}
          </p>
          <p v-if="log.details.criminalDice">
            <i class="fas fa-dice mr-1 text-indigo-400"></i> ผลลูกเต๋าอาชญากร: {{ log.details.criminalDice }}
          </p>
          <p v-if="log.details.isEncountered !== undefined">
            <i class="fas fa-eye mr-1 text-indigo-400"></i> พบเป้าหมาย: {{ log.details.isEncountered ? 'ใช่' : 'ไม่' }}
          </p>
          <p v-if="log.details.damageToCriminal">
            <i class="fas fa-burst mr-1 text-indigo-400"></i> ดาเมจที่ทำต่ออาชญากร: {{ log.details.damageToCriminal }}
          </p>
          <p v-if="log.details.currentCriminalHp !== undefined">
            <i class="fas fa-heart mr-1 text-indigo-400"></i> HP อาชญากรคงเหลือ: {{ log.details.currentCriminalHp }}
          </p>
          <p v-if="log.details.isCriminalDead">
            <i class="fas fa-skull-crossbones mr-1 text-indigo-400"></i> อาชญากรตาย: {{ log.details.isCriminalDead ? 'ใช่' : 'ไม่' }}
          </p>
          <p v-if="log.details.rewards && Object.keys(log.details.rewards).length > 0">
            <i class="fas fa-gift mr-1 text-indigo-400"></i> รางวัลที่ได้รับ: {{ formatResources(log.details.rewards) }}
          </p>
          <p v-if="log.details.usedEnergy">
            <i class="fas fa-bolt mr-1 text-indigo-400"></i> พลังงานที่ใช้: {{ log.details.usedEnergy }}
          </p>
          <p v-if="log.details.newEnergy">
            <i class="fas fa-bolt mr-1 text-indigo-400"></i> พลังงานคงเหลือ: {{ log.details.newEnergy }}
          </p>
          <!-- Punish Details -->
          <p v-if="log.details.characterId">
            <i class="fas fa-user mr-1 text-indigo-400"></i> รหัสตัวละคร: {{ log.details.characterId }}
          </p>
          <p v-if="log.details.itemsToLoseCount !== undefined">
            <i class="fas fa-suitcase mr-1 text-indigo-400"></i> จำนวนไอเทมที่สูญเสีย: {{ log.details.itemsToLoseCount }}
          </p>
          <p v-if="log.details.skillsToLoseCount !== undefined">
            <i class="fas fa-book mr-1 text-indigo-400"></i> จำนวนทักษะที่สูญเสีย: {{ log.details.skillsToLoseCount }}
          </p>
          <p v-if="log.details.tattoosToLoseCount !== undefined">
            <i class="fas fa-tattoo mr-1 text-indigo-400"></i> จำนวนลายสักที่สูญเสีย: {{ log.details.tattoosToLoseCount }}
          </p>
          <p v-if="log.details.roomsToLoseCount !== undefined">
            <i class="fas fa-door-open mr-1 text-indigo-400"></i> จำนวนห้องที่สูญเสีย: {{ log.details.roomsToLoseCount }}
          </p>
          <p v-if="log.details.bagItemsToLoseCount !== undefined">
            <i class="fas fa-backpack mr-1 text-indigo-400"></i> จำนวนไอเทมในกระเป๋าที่สูญเสีย: {{ log.details.bagItemsToLoseCount }}
          </p>
          <!-- Reborn Details -->
          <p v-if="log.details.lostItems && log.details.lostItems.length > 0">
            <i class="fas fa-suitcase mr-1 text-indigo-400"></i> ไอเทมที่สูญเสีย: {{ log.details.lostItems.join(', ') }}
          </p>
          <p v-if="log.details.lostTattoos && log.details.lostTattoos.length > 0">
            <i class="fas fa-tattoo mr-1 text-indigo-400"></i> ลายสักที่สูญเสีย: {{ log.details.lostTattoos.join(', ') }}
          </p>
          <p v-if="log.details.lostSkills && log.details.lostSkills.length > 0">
            <i class="fas fa-book mr-1 text-indigo-400"></i> ทักษะที่สูญเสีย: {{ log.details.lostSkills.join(', ') }}
          </p>
          <p v-if="log.details.lostBagItems && log.details.lostBagItems.length > 0">
            <i class="fas fa-backpack mr-1 text-indigo-400"></i> ไอเทมในกระเป๋าที่สูญเสีย: {{ log.details.lostBagItems.join(', ') }}
          </p>
          <p v-if="log.details.roomsLost !== undefined">
            <i class="fas fa-door-open mr-1 text-indigo-400"></i> จำนวนห้องที่สูญเสีย: {{ log.details.roomsLost }}
          </p>
          <p v-if="log.details.newRoomCount !== undefined">
            <i class="fas fa-door-open mr-1 text-indigo-400"></i> จำนวนห้องใหม่: {{ log.details.newRoomCount }}
          </p>
          <p v-if="log.details.newInventoryCount !== undefined">
            <i class="fas fa-backpack mr-1 text-indigo-400"></i> จำนวนไอเทมในกระเป๋าใหม่: {{ log.details.newInventoryCount }}
          </p>
          <p v-if="log.details.newBagWeight !== undefined">
            <i class="fas fa-weight-hanging mr-1 text-indigo-400"></i> น้ำหนักกระเป๋าใหม่: {{ log.details.newBagWeight }}
          </p>
          <p v-if="log.details.newEnergyLevel !== undefined">
            <i class="fas fa-bolt mr-1 text-indigo-400"></i> พลังงานใหม่: {{ log.details.newEnergyLevel }}
          </p>
          <p v-if="log.details.newFoodLevel !== undefined">
            <i class="fas fa-bread-slice mr-1 text-indigo-400"></i> ระดับอาหารใหม่: {{ log.details.newFoodLevel }}
          </p>
          <p v-if="log.details.newHappyLevel !== undefined">
            <i class="fas fa-smile mr-1 text-indigo-400"></i> ระดับความสุขใหม่: {{ log.details.newHappyLevel }}
          </p>
          <p v-if="log.details.newInfectionLevel !== undefined">
            <i class="fas fa-biohazard mr-1 text-indigo-400"></i> ระดับการติดเชื้อใหม่: {{ log.details.newInfectionLevel }}
          </p>
          <p v-if="log.details.newDamageTaken !== undefined">
            <i class="fas fa-heart-broken mr-1 text-indigo-400"></i> ความเสียหายใหม่: {{ log.details.newDamageTaken }}
          </p>
          <!-- Storage and Bag Upgrade Details -->
          <p v-if="log.details.cashUsed">
            <i class="fas fa-coins mr-1 text-indigo-400"></i> แคชที่ใช้: {{ log.details.cashUsed }}
          </p>
          <p v-if="log.details.previousMaxLoad">
            <i class="fas fa-box mr-1 text-indigo-400"></i> ความจุก่อนอัปเกรด: {{ log.details.previousMaxLoad }}
          </p>
          <p v-if="log.details.newMaxLoad">
            <i class="fas fa-box mr-1 text-indigo-400"></i> ความจุหลังอัปเกรด: {{ log.details.newMaxLoad }}
          </p>
          <p v-if="log.details.newCashBalance">
            <i class="fas fa-wallet mr-1 text-indigo-400"></i> แคชคงเหลือ: {{ log.details.newCashBalance }}
          </p>
          <!-- Move Room Details -->
          <p v-if="log.details.previousRoom">
            <i class="fas fa-door-open mr-1 text-indigo-400"></i> ห้องก่อนย้าย: {{ log.details.previousRoom }}
          </p>
          <p v-if="log.details.newRoom">
            <i class="fas fa-door-open mr-1 text-indigo-400"></i> ห้องใหม่: {{ log.details.newRoom }}
          </p>
          <p v-if="log.details.totalRooms">
            <i class="fas fa-door-open mr-1 text-indigo-400"></i> จำนวนห้องทั้งหมด: {{ log.details.totalRooms }}
          </p>
          <p v-if="log.details.requestedRoom">
            <i class="fas fa-door-open mr-1 text-indigo-400"></i> ห้องที่ต้องการย้าย: {{ log.details.requestedRoom }}
          </p>
          <p v-if="log.details.currentRoom">
            <i class="fas fa-door-open mr-1 text-indigo-400"></i> ห้องปัจจุบัน: {{ log.details.currentRoom }}
          </p>
          <!-- PvP Details -->
          <p v-if="log.details.attackerName">
            <i class="fas fa-user-secret mr-1 text-indigo-400"></i> ผู้โจมตี: {{ log.details.attackerName }}
          </p>
          <p v-if="log.details.survivorName">
            <i class="fas fa-user-secret mr-1 text-indigo-400"></i> ผู้รอดชีวิต: {{ log.details.survivorName }}
          </p>
          <p v-if="log.details.damageToSurvivor">
            <i class="fas fa-burst mr-1 text-indigo-400"></i> ดาเมจที่ทำต่อผู้รอดชีวิต: {{ log.details.damageToSurvivor }}
          </p>
          <p v-if="log.details.damageToPlayer">
            <i class="fas fa-shield-alt mr-1 text-indigo-400"></i> ดาเมจที่ผู้โจมตีได้รับ: {{ log.details.damageToPlayer }}
          </p>
          <p v-if="log.details.isSurvivorDead">
            <i class="fas fa-skull-crossbones mr-1 text-indigo-400"></i> ผู้รอดชีวิตตาย: {{ log.details.isSurvivorDead ? 'ใช่' : 'ไม่' }}
          </p>
          <p v-if="log.details.isPlayerDead">
            <i class="fas fa-skull-crossbones mr-1 text-indigo-400"></i> ผู้โจมตีตาย: {{ log.details.isPlayerDead ? 'ใช่' : 'ไม่' }}
          </p>
          <!-- Error Message -->
          <p v-if="log.error && !log.success" class="error-message text-red-300">
            <i class="fas fa-exclamation-circle mr-1"></i> ข้อผิดพลาด: {{ log.error }}
          </p>
        </div>
        <!-- Warning for High Infection Level, Low Energy, or Low Cash -->
        <div
          v-if="checkWarnings(log)"
          class="warning-message mt-2 p-2 bg-red-800 text-red-100 rounded-md text-sm animate-pulse"
        >
          {{ checkWarnings(log).message }}
        </div>
      </div>
      <div v-if="!loading && hasMore && logs.length > 0" class="load-more-container text-center mt-4">
        <button @click="loadMore" class="load-more-btn bg-indigo-600 text-white py-2 px-4 rounded-md transition-all duration-300 hover:bg-indigo-500 hover:shadow-lg">
          โหลดเพิ่มเติม
        </button>
      </div>
      <div v-if="loadingMore" class="loading-more text-center mt-4 text-indigo-200 text-sm animate-pulse">
        กำลังโหลดเพิ่มเติม...
      </div>
    </div>
  </div>
</template>

<script>
import { projectFirestore } from '@/firebase/config';
import getUser from '@/composables/getUser';
import { ref, computed, onMounted } from 'vue';

export default {
  setup() {
    const { user } = getUser();
    const logs = ref([]);
    const loading = ref(true);
    const loadingMore = ref(false);
    const hasMore = ref(true);
    const lastDoc = ref(null);
    const limitSize = 30;

    // Fetch initial logs
    const fetchLogs = async () => {
      if (!user.value?.uid) {
        loading.value = false;
        return;
      }

      try {
        let query = projectFirestore
          .collection('logs')
          .where('uid', '==', user.value.uid)
          .orderBy('timestamp', 'desc')
          .limit(limitSize);

        const snapshot = await query.get();

        logs.value = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        if (snapshot.docs.length < limitSize) {
          hasMore.value = false;
        } else {
          lastDoc.value = snapshot.docs[snapshot.docs.length - 1];
        }

        loading.value = false;
      } catch (error) {
        console.error('Error fetching logs:', error);
        loading.value = false;
      }
    };

    // Load more logs
    const loadMore = async () => {
      if (!hasMore.value || loadingMore.value) return;

      loadingMore.value = true;

      try {
        let query = projectFirestore
          .collection('logs')
          .where('uid', '==', user.value.uid)
          .orderBy('timestamp', 'desc')
          .startAfter(lastDoc.value)
          .limit(limitSize);

        const snapshot = await query.get();

        const newLogs = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        logs.value = [...logs.value, ...newLogs];

        if (snapshot.docs.length < limitSize) {
          hasMore.value = false;
        } else {
          lastDoc.value = snapshot.docs[snapshot.docs.length - 1];
        }
      } catch (error) {
        console.error('Error loading more logs:', error);
      } finally {
        loadingMore.value = false;
      }
    };

    // Format timestamp
    const formatTimestamp = (timestamp) => {
      if (!timestamp) return 'ไม่ระบุ';
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return date.toLocaleString('th-TH', {
        dateStyle: 'medium',
        timeStyle: 'short',
      });
    };

    // Get action name
    const getActionName = (action) => {
      const actions = {
        explore: 'ผจญภัย',
        new_user_signup: 'สมัครสมาชิก',
        create_character: 'สร้างตัวละคร',
        upgrade_stats: 'อัปเกรดสเตตัส',
        attack_zombie: 'โจมตีซอมบี้',
        flee_fight: 'หนีการต่อสู้',
        claim_rewards: 'รับรางวัล',
        go_home: 'กลับบ้าน',
        add_to_bag: 'ใส่ไอเทมลงกระเป๋า',
        discard_item: 'ทิ้งไอเทม',
        equip_item: 'ใส่ไอเทม',
        unequip_item: 'ถอนไอเทม',
        move_item_to_storage: 'ย้ายไอเทมไปคลัง',
        move_item_to_bag: 'ย้ายไอเทมไปกระเป๋า',
        change_trait: 'เปลี่ยนลักษณะนิสัย',
        learn_skill: 'เรียนรู้ทักษะ',
        tattoo_skin: 'สักลาย',
        unlock_title: 'ปลดล็อกฉายา',
        use_title: 'ใช้งานฉายา',
        heal_health: 'รักษาเลือด',
        heal_infection: 'รักษาการติดเชื้อ',
        sell_item: 'ขายไอเทม',
        buy_item: 'ซื้อไอเทม',
        buy_food: 'ซื้ออาหาร',
        unlock_room: 'ปลดล็อคห้อง',
        assassinate_criminal: 'ลอบสังหารอาชญากร',
        punish: 'ลงโทษ',
        reborn: 'เกิดใหม่',
        upgrade_storage: 'อัปเกรดคลัง',
        upgrade_bag: 'อัปเกรดกระเป๋า',
        move_room: 'ย้ายห้อง',
        pvp: 'การต่อสู้ PvP',
        depletionDeath: 'เสียชีวิต',
        sell_items: 'ขายไอเทม',
        energyIncrease: 'เพิ่มพลังงาน',
        claim_mysterious_item: 'สุ่มรับไอเทมคลังปริศนา',
        claim_mysterious_item_with_diamonds: 'สุ่มรับไอเทมกล่องปริศนาด้วยแคช',
        claim_mysterious_item_free: 'สุ่มรับไอเทมกล่องปริศนาด้วยฟรี',
        healReset: 'รีเซ็ตฮีล'
      };
      return actions[action] || action;
    };

    // Get zombie name
    const getZombieName = (zombieName) => {
      const zombieNames = {
        WeakZombie: 'ซอมบี้อ่อนแอ',
        FastZombie: 'ซอมบี้ว่องไว',
        StrongZombie: 'ซอมบี้แข็งแกร่ง',
      };
      return zombieNames[zombieName] || zombieName;
    };

    // Format resources
    const formatResources = (resources) => {
      const resourceNames = {
        cash: 'แคช',
        energyLevel: 'พลังงาน',
        happyLevel: 'ความสุข',
        diamond: 'เพชร',
        gold: 'ทอง',
        silver: 'เหรียญเงิน',
        renown: 'ชื่อเสียง',
        diamondGained: 'เพชรที่ได้รับ',
        goldGained: 'ทองที่ได้รับ',
        diamondLost: 'เพชรที่เสีย',
        goldLost: 'ทองที่เสีย',
        diamondToSurvivor: 'เพชรให้ผู้รอดชีวิต',
        goldToSurvivor: 'ทองให้ผู้รอดชีวิต',
        diamondLostBySurvivor: 'เพชรที่ผู้รอดชีวิตเสีย',
        goldLostBySurvivor: 'ทองที่ผู้รอดชีวิตเสีย',
      };
      return Object.entries(resources)
        .map(([key, value]) => `${resourceNames[key] || key} ${value}`)
        .join(', ');
    };

    // Format item stats
    const formatItemStats = (stats) => {
      const statNames = {
        stamina: 'ความแข็งแกร่ง',
        str: 'พลัง',
        int: 'สติปัญญา',
        agi: 'ความว่องไว',
        maxHp: 'HP สูงสุด',
        atk: 'พลังโจมตี',
        evasion: 'การหลบหลีก',
        critical: 'คริติคอล',
        defense: 'การป้องกัน',
      };
      return Object.entries(stats)
        .filter(([_, value]) => value !== 0)
        .map(([key, value]) => `${statNames[key] || key}: ${value}`)
        .join(', ');
    };

    // Format currency
    const formatCurrency = (currency, amount) => {
      const currencyNames = {
        cash: 'แคช',
        diamond: 'เพชร',
        gold: 'ทอง',
        silver: 'เหรียญเงิน',
      };
      return `${amount} ${currencyNames[currency] || currency}`;
    };

    // Format balances
    const formatBalances = (balances) => {
      const currencyNames = {
        cash: 'แคช',
        diamond: 'เพชร',
        gold: 'ทอง',
        silver: 'เหรียญเงิน',
      };
      return Object.entries(balances)
        .map(([key, value]) => `${currencyNames[key] || key}: ${value}`)
        .join(', ');
    };

    const checkWarnings = (log) => {
  // ป้องกัน error ทันทีถ้าไม่มี details
  if (!log?.details) return null;

  const { details } = log;

  if (details.infectionLevel >= 80 || details.previousInfectionLevel >= 80) {
    return { type: 'warning', message: 'ระดับการติดเชื้อสูง! รักษาโดยด่วน' };
  }

  if (details.newFoodLevel <= 20) {
    return { type: 'warning', message: 'ระดับอาหารต่ำ! ซื้ออาหารเพิ่มเติม' };
  }

  if (details.newHappyLevel <= 20) {
    return { type: 'warning', message: 'ระดับความสุขต่ำ! ซื้ออาหารที่มีความสุขสูงขึ้น' };
  }

  // พลังงาน (รองรับทั้ง newEnergy และ newEnergyLevel)
  if ((details.newEnergy ?? 999) <= 20 || (details.newEnergyLevel ?? 999) <= 20) {
    return { type: 'warning', message: 'พลังงานต่ำ! พักผ่อนหรือใช้ไอเทมเพิ่มพลังงาน' };
  }

  if ((details.newCashBalance ?? 999) <= 20) {
    return { type: 'warning', message: 'แคชเหลือน้อย! หาแคชเพิ่มเติม' };
  }

  return null;
};

    onMounted(() => {
      fetchLogs();
    });

    return {
      logs,
      loading,
      loadingMore,
      hasMore,
      formatTimestamp,
      getActionName,
      getZombieName,
      formatResources,
      formatItemStats,
      formatCurrency,
      formatBalances,
      checkWarnings,
      user,
      loadMore
    };
  },
};
</script>

<style scoped>
.animate-fade-in {
  animation: fadeIn 0.5s ease-in-out;
}
@keyframes fadeIn {
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
}
.animate-pulse {
  animation: pulse 1.5s infinite;
}
@keyframes pulse {
  0% { opacity: 1; }
  50% { opacity: 0.7; }
  100% { opacity: 1; }
}
#style-1::-webkit-scrollbar {
  width: 5px;
}
#style-1::-webkit-scrollbar-track {
  background: #374151;
  border-radius: 10px;
}
#style-1::-webkit-scrollbar-thumb {
  background: #4b5563;
  border-radius: 10px;
}
#style-1::-webkit-scrollbar-thumb:hover {
  background: #6b7280;
}
#style-1 {
  scrollbar-width: thin;
  scrollbar-color: #4b5563 #374151;
}
button:not(:disabled):active {
  transform: scale(0.98);
}
.bg {
  background: url('https://firebasestorage.googleapis.com/v0/b/zombiekub.firebasestorage.app/o/bg%2Fdocumentroom.png?alt=media&token=5d173b7e-d5a5-423c-a12d-630945a52f66') no-repeat center center fixed,
              linear-gradient(to bottom, rgba(10, 10, 10, 0.95), rgba(20, 20, 20, 0.98));
}
.log-card {
  background: linear-gradient(135deg, #1f2937 0%, #111827 100%);
}
.warning-message {
  background: linear-gradient(135deg, #7f1d1d 0%, #991b1b 100%);
}
</style>